/* eslint-disable react/jsx-wrap-multilines */
import { Constants, TextUtilities } from '@pwc/pwc-deals-web-common';
import cloneDeep from 'lodash/cloneDeep';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import RichTextForm from 'common/components/RichTextForm/RichTextForm';
import { useSections } from 'features/analyses/Freeform/FreeformAnalysisForm/FreeformAnalysisFormContext';
import { SECTION_STATE_ACTION } from 'features/analyses/Freeform/FreeformAnalysisForm/FreeformAnalysisFormUtil/sectionsReducer';
import { updateLibraryItem } from 'features/analyses/libraryItemsSlice';
import './RichTextLibraryItemCard.scss';
import { isPowerPivotLibraryItem } from './PowerPivot/constants/PowerPivotTypes';
import PowerPivotNotSupportedModal from './PowerPivot/components/PowerPivotNotSupportedModal';
import {
  TEXT_AREA,
  UNABLE_TO_ADD_CONTENT,
} from './PowerPivot/constants/PowerPivotErrorMessages';
import {
  setShowPowerPivotNotSupportedModal,
  setShowSharePointModalPowerPivot,
} from './PowerPivot/redux/powerPivotSlice';
import { handleGenericWidgetsItems } from '../../../contentLibrary/libraryItemService';

/**
 * Component usage: rich text editor in freeform analysis in edit mode
 */
const RichTextLibraryItemCard = ({
  libraryItem,
  setIsDragDisabled,
  innerRef,
  isDragging,
  ...props
}) => {
  const refEditor = useRef(undefined);
  const dispatch2 = useDispatch();
  const richTextParsed = useMemo(() => {
    return TextUtilities.parseRichText(
      libraryItem.isShared,
      libraryItem.data
    ).replace(Constants.EMPTY_RICH_TEXT_PLACEHOLDER, '');
  }, [libraryItem.data, libraryItem.isShared]);

  const [, dispatch] = useSections();
  const [currentRichText, setCurrentRichText] = useState(richTextParsed);
  const setEditor = useCallback(
    (editor) => {
      refEditor.current = editor;
    },
    [refEditor]
  );

  const handleBlur = () => {
    setIsDragDisabled(false);
  };

  const handleChange = async (value) => {
    setCurrentRichText(value);
    const content2 = new DOMParser().parseFromString(value, 'text/html');
    const placeholders2 = content2.getElementsByClassName('placeholder');
    const linkedObjects2 = [];
    let containsMiniloader = false;

    if (refEditor.current) {
      handleGenericWidgetsItems({
        editor: refEditor.current,
        list: content2.querySelectorAll('[data-widget-name]'),
        targetAdd: linkedObjects2,
      });
    }

    // eslint-disable-next-line no-restricted-syntax
    for (const placeholder of placeholders2) {
      const { id } = placeholder;
      if (id !== 'miniloader') {
        // eslint-disable-next-line no-console,no-underscore-dangle
        const raw = window.__LIBRARYITEMSTORE__.get(id);
        const { renderedSvg, rendered } = raw;

        dispatch2(updateLibraryItem({ id, item: renderedSvg || rendered }));
        const libraryItemL = cloneDeep(raw);
        if (libraryItemL) {
          if (isPowerPivotLibraryItem(libraryItemL.type)) {
            dispatch2(setShowPowerPivotNotSupportedModal(true));
            return;
          }
          linkedObjects2.push({ ...libraryItemL, rendered: undefined });
        }
      } else {
        containsMiniloader = true;
      }
    }
    const noPlaceholders = !placeholders2 || placeholders2?.length === 0;
    if (noPlaceholders || containsMiniloader) {
      // if there is no library item then it displays the old version Share Point modal without Power Pivot
      dispatch2(setShowSharePointModalPowerPivot(false));
    }
    dispatch({
      type: SECTION_STATE_ACTION.UPDATE_RICH_TEXT,
      payload: {
        libraryItemId: libraryItem.id,
        richText: content2.body.innerHTML,
        linkedObjects: linkedObjects2,
      },
    });
  };

  useEffect(() => {
    setIsDragDisabled(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragging]);

  const className = `rich-text-library-item-edit css-override ${
    isDragging ? 'dragging' : ''
  }`;

  const { t } = useTranslation();
  return (
    <div ref={innerRef} {...props} className={className}>
      <RichTextForm
        libraryItemId={libraryItem.id}
        onBlur={handleBlur}
        onFocus={() => {
          setIsDragDisabled(true);
        }}
        richText={currentRichText}
        setCurrentRichText={handleChange}
        setEditor={setEditor}
      />

      <PowerPivotNotSupportedModal
        title={t(UNABLE_TO_ADD_CONTENT)}
        message={t(TEXT_AREA)}
      />
    </div>
  );
};

export default RichTextLibraryItemCard;
